import React, { useContext, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import {
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Card,
  CardContent,
  Avatar,
  Box,
  TextField,
  InputAdornment,
  Divider,
  Collapse
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

// Ícones
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import SearchIcon from '@material-ui/icons/Search';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import { Autocomplete } from '@material-ui/lab';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import UIFormTitle from '../../../UI/Form/Title/UIFormTitle';
import StoreContext from '../../../Store/context';
import useApi from '../../../../utils/useApi';
import UIDialogTitle from '../../../UI/Dialog/Title/UIDialogTitle';
import { emptyObject, formattedCurrency } from '../../../../utils/utils';
import UIDialogConfirm from '../../../UI/Dialog/Confirm/UIDialogConfirm';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  cardCashier: {
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    border: '1px solid transparent',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
    position: 'relative',
    overflow: 'visible'
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  valueText: {
    color: theme.palette.success.main,
    fontWeight: 'bold'
  },
  searchBar: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#fff'
  },
  // Estilos da área de previsão
  predictionContainer: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  predictionHeader: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontWeight: 'bold',
    marginBottom: theme.spacing(1.5),
    display: 'block'
  },
  predictionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  balanceFlow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    gap: theme.spacing(1)
  },
  oldBalance: {
    textDecoration: 'line-through',
    color: theme.palette.text.disabled
  },
  arrowIcon: {
    fontSize: '1rem',
    color: theme.palette.text.secondary
  },
  // Estilo do botão de opções avançadas
  advancedButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    '&:hover': {
        color: theme.palette.primary.main,
    }
  }
}));

const TransferNew = (props) => {
  const { usersInfo, cashierInfo, goToHistory, mobile } = props;
  const classes = useStyles();
  const { credencials } = useContext(StoreContext);

  const [modalTransfer, setModalTransfer] = useState(false);
  const [modalValues, setModalValues] = useState(null);
  const [alertTransfer, setAlertTransfer] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para controlar a exibição das opções avançadas
  const [advancedSetting, setAdvancedSetting] = useState(false);

  // Carrega caixas ativos do usuário
  const [myCashiersLoad, myCashiersInfo] = useApi({
    debounceDelay: 500,
    method: 'get',
    url: '/cash-registers',
    params: { isActive: 1, userId: Number(credencials.id) },
  });

  const [transferLoad, transferInfo, setTransferInfo] = useApi({
    method: 'post',
    url: '/cash-registers/transfers',
  });

  useEffect(() => {
    myCashiersLoad();
  }, []);

  function handleModalTransfer(cashier) {
    if (cashier) {
      setModalValues({
        sender: cashier.id,
        senderName: cashier.name,
        receiver: null,
        requestedValue: 0,
        currentValue: cashier.currentValue, // Para validação visual
        approver: null,
        notification: '',
        comment: '',
      });
      setAdvancedSetting(false); // Reseta para fechado sempre que abrir o modal
      setModalTransfer(true);
    } else {
      setModalValues(null);
      setModalTransfer(false);
    }
  }

  function onTransfer(values) {
    const data = {
      sender: values.sender,
      receiver: values.receiver && values.receiver.id,
      requestedValue: values.requestedValue,
      approver: values.approver && values.approver.id,
      notification: values.notification,
      comment: values.comment,
    };

    transferLoad({
      data: emptyObject(data),
      onCompleted: (res) => {
        setAlertTransfer(true);
        if (res) {
          handleModalTransfer(null);
          myCashiersLoad(); // Atualiza valores
        }
      },
    });
  }

  // Filtragem local
  const filteredCashiers = myCashiersInfo.data
    ? myCashiersInfo.data.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className={classes.root}>
      <UIFormTitle title="Selecione o caixa de origem" />
      
      <TextField 
        variant="outlined"
        placeholder="Buscar caixa..."
        fullWidth
        size="small"
        className={classes.searchBar}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
        }}
      />

      {myCashiersInfo.loading && <CircularProgress />}
      
      <Grid container spacing={2}>
        {filteredCashiers.map((cashier) => (
          <Grid item xs={12} md={6} lg={4} key={cashier.id}>
            <Card className={classes.cardCashier} onClick={() => handleModalTransfer(cashier)}>
               <CardContent className={classes.cardContent}>
                  <Avatar className={classes.avatar}>
                    <AccountBalanceWalletIcon />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="h6" component="div">
                       {cashier.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                       Saldo atual: <span className={classes.valueText}>{formattedCurrency(cashier.currentValue)}</span>
                    </Typography>
                  </Box>
                  <ArrowForwardIcon color="action" />
               </CardContent>
            </Card>
          </Grid>
        ))}
        
        {!myCashiersInfo.loading && filteredCashiers.length === 0 && (
            <Grid item xs={12}>
                <Typography align="center" color="textSecondary">
                    Nenhum caixa ativo encontrado para você.
                </Typography>
            </Grid>
        )}
      </Grid>

      {/* MODAL DE TRANSFERÊNCIA */}
      {modalValues && (
        <Dialog open={modalTransfer} onClose={() => handleModalTransfer(null)} fullWidth maxWidth="sm">
          <Formik initialValues={modalValues} onSubmit={onTransfer}>
            {({ setFieldValue, values, errors }) => {
                
                // Lógica de Previsão de Saldo
                const senderCurrent = Number(values.currentValue) || 0;
                const receiverCurrent = values.receiver ? (Number(values.receiver.currentValue) || 0) : 0;
                const transferVal = Number(values.requestedValue) || 0;
                
                const senderPredicted = senderCurrent - transferVal;
                const receiverPredicted = receiverCurrent + transferVal;

                return (
                  <Form>
                    <UIDialogTitle onClose={() => handleModalTransfer(null)}>
                      Sangria: {values.senderName}
                    </UIDialogTitle>
                    <DialogContent dividers>
                      <Grid container spacing={2}>
                        {/* Campos Essenciais */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={cashierInfo.data ? cashierInfo.data.filter(c => c.id !== values.sender) : []}
                                getOptionLabel={(option) => option.name}
                                onChange={(e, v) => setFieldValue('receiver', v)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Caixa de Destino" variant="outlined" fullWidth />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                label="Valor da Sangria"
                                variant="outlined"
                                fullWidth
                                currencySymbol="R$"
                                decimalCharacter=","
                                digitGroupSeparator="."
                                onChange={(e, value) => setFieldValue('requestedValue', value)}
                                value={values.requestedValue}
                            />
                        </Grid>

                        {/* Área de Previsão de Saldos */}
                        {values.receiver && (
                            <Grid item xs={12}>
                                <div className={classes.predictionContainer}>
                                    <Typography className={classes.predictionHeader}>
                                        Previsão de Saldos
                                    </Typography>

                                    {/* Origem */}
                                    <div className={classes.predictionRow}>
                                        <Typography variant="body2" color="textSecondary">
                                            {values.senderName} (Origem)
                                        </Typography>
                                        <div className={classes.balanceFlow}>
                                            <span className={classes.oldBalance}>{formattedCurrency(senderCurrent)}</span>
                                            <TrendingFlatIcon className={classes.arrowIcon} />
                                            <span style={{ fontWeight: 'bold', color: senderPredicted < 0 ? '#f44336' : 'inherit' }}>
                                                {formattedCurrency(senderPredicted)}
                                            </span>
                                        </div>
                                    </div>

                                    <Divider style={{ margin: '8px 0' }} />

                                    {/* Destino */}
                                    <div className={classes.predictionRow}>
                                        <Typography variant="body2" color="textSecondary">
                                            {values.receiver.name} (Destino)
                                        </Typography>
                                        <div className={classes.balanceFlow}>
                                            <span className={classes.oldBalance}>{formattedCurrency(receiverCurrent)}</span>
                                            <TrendingFlatIcon className={classes.arrowIcon} />
                                            <span style={{ fontWeight: 'bold', color: '#4caf50' }}>
                                                {formattedCurrency(receiverPredicted)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Grid>
                        )}

                        {/* Botão para expandir opções avançadas */}
                        <Grid item xs={12}>
                            <Box 
                                className={classes.advancedButton}
                                onClick={() => setAdvancedSetting(!advancedSetting)}
                            >
                                <Typography variant="button">
                                    {advancedSetting ? 'Ocultar opções avançadas' : 'Mostrar opções avançadas'}
                                </Typography>
                                {advancedSetting ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                        </Grid>

                        {/* Campos Opcionais (Recolhidos) */}
                        <Grid item xs={12}>
                            <Collapse in={advancedSetting}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField 
                                            label="Observação (Opcional)"
                                            variant="outlined"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            onChange={(e) => setFieldValue('comment', e.target.value)}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <Typography variant="overline" display="block" gutterBottom>
                                            Aprovação (Opcional)
                                        </Typography>
                                        <Autocomplete
                                            // Filtra o próprio usuário da lista de aprovadores para evitar auto-aprovação
                                            options={usersInfo.data ? usersInfo.data.filter(u => u.id !== Number(credencials.id)) : []}
                                            getOptionLabel={(option) => option.name}
                                            onChange={(e, v) => setFieldValue('approver', v)}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Usuário Aprovador" variant="outlined" size="small" fullWidth />
                                            )}
                                        />
                                        {/* Alerta Condicional - Só aparece se um aprovador for selecionado */}
                                        {values.approver && (
                                            <Box mt={1}>
                                                <Alert severity="warning">
                                                    Atenção: Ao selecionar um aprovador, a transferência só será efetivada mediante aprovação do usuário selecionado.
                                                </Alert>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>
                      </Grid>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => handleModalTransfer(null)}>Cancelar</Button>
                      <Button 
                        type="submit" 
                        color="primary" 
                        variant="contained"
                        disabled={!values.receiver || values.requestedValue <= 0}
                      >
                        Confirmar Sangria
                      </Button>
                    </DialogActions>
                  </Form>
                );
            }}
          </Formik>
        </Dialog>
      )}

      <UIDialogConfirm
        open={alertTransfer}
        close={() => setAlertTransfer(false)}
        data={transferInfo}
        messageSucess="Solicitação de sangria realizada!"
        setrequestinfo={setTransferInfo}
      />
    </div>
  );
};

export default TransferNew;